// <jgd-option-list> — collapsible option value list for the jgd variant picker
// (block setting "sizes_display", modes "count" and "rows"; color tiles use
// mode "row").
//
// mode="count": the snippet decides server-side which items get the
//   .jgd-option-hidden class and renders the "+N" label — this module only
//   toggles the expanded state.
// mode="rows": this module measures the wrapped list and hides everything
//   beyond `max-rows` rows. The checked value is kept visible, and if all
//   values fit without the "+N" button nothing is hidden at all (so a single
//   value is never replaced by "+1").
// mode="row": color tiles in a single line. The baseline (pre-measurement,
//   no-JS) is a scrollable row showing every tile; measuring hides the tiles
//   that don't fit. All tiles are equally sized, so the fit is plain
//   geometry — no button-width probing: the tile-sized in-row "+N" takes
//   exactly one tile slot, while the text-label variants render the control
//   below the row and don't compete for row space at all. When everything
//   fits, no control shows (a false "+1" is impossible by construction).
//
// Expanded state survives the section re-render after a variant change via a
// module-level Set (same pattern as jgd-variant-picker.js).

const expandedKeys = new Set();

class JgdOptionList extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** Guards against re-entrant measuring (our own writes resize the element). */
  #measuring = false;

  /** @returns {boolean} true for the modes that measure client-side */
  get #measures() {
    const mode = this.getAttribute('mode');
    return mode === 'rows' || mode === 'row';
  }

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.sync();
    if (this.#measures) {
      // Also covers lists rendered inside a closed dialog (quick-add): the
      // first real layout changes the size from 0 and triggers a measure.
      this.#resizeObserver = new ResizeObserver(() => this.#measure());
      this.#resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** Re-apply remembered expansion and, in the measuring modes, re-measure. */
  sync() {
    if (expandedKeys.has(this.#key)) this.setAttribute('expanded', '');
    else this.removeAttribute('expanded');
    if (this.#measures) this.#measure();
  }

  /** @returns {string} key that stays stable across section re-renders */
  get #key() {
    const picker = this.closest('variant-picker');
    const fieldset = this.closest('fieldset');
    return `${picker?.dataset.blockId}:${fieldset?.dataset.fieldsetIndex}`;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.jgd-variant-show-all')) {
      event.preventDefault();
      this.#expand();
    } else if (event.target.closest('.jgd-variant-show-less')) {
      event.preventDefault();
      this.#collapse();
    }
  };

  #expand() {
    const firstHiddenInput = this.querySelector('li.jgd-option-hidden input');
    this.setAttribute('expanded', '');
    expandedKeys.add(this.#key);
    firstHiddenInput?.focus();
  }

  #collapse() {
    this.removeAttribute('expanded');
    expandedKeys.delete(this.#key);
    if (this.#measures) this.#measure();
    requestAnimationFrame(() => this.querySelector('.jgd-variant-show-all')?.focus());
  }

  #measure() {
    if (this.getAttribute('mode') === 'row') this.#measureRow();
    else this.#measureRows();
  }

  // --- rows mode -------------------------------------------------------------

  #measureRows() {
    if (this.#measuring) return;
    if (this.getAttribute('mode') !== 'rows') return;
    if (this.hasAttribute('expanded')) return;
    // Not laid out yet (e.g. inside a still-closed quick-add dialog); the
    // ResizeObserver re-runs this once the element gets real dimensions.
    if (this.clientWidth === 0) return;

    const items = [...this.querySelectorAll('li.jgd-variant-option__item')];
    const moreItem = this.querySelector('li.jgd-option-list__more');
    const moreButton = moreItem?.querySelector('button');
    if (!items.length || !moreItem || !moreButton) return;

    this.#measuring = true;

    const maxRows = Math.max(1, parseInt(this.getAttribute('max-rows') ?? '', 10) || 2);
    // static-more-label: the button carries a server-rendered text label
    // ("show more") that must not be replaced with a "+N" counter.
    // more-label: text label that gets the live count appended ("… (+3)").
    const staticLabel = this.hasAttribute('static-more-label');
    const labelBase = this.getAttribute('more-label');
    /** @param {number} count */
    const moreLabel = (count) => (labelBase ? `${labelBase} (+${count})` : `+${count}`);

    // Pass 1: does everything fit without the button? Then hide nothing —
    // this also implements the "never hide just one value" rule.
    for (const item of items) item.classList.remove('jgd-option-hidden');
    moreItem.hidden = true;
    if (this.#rowCount(items) <= maxRows) {
      this.#measuring = false;
      return;
    }

    // Pass 2: reserve the button, hide from the geometric cutoff on (keeping
    // the checked value in the last visible slot) and shrink until the button
    // sits within the allowed rows.
    if (!staticLabel) moreButton.textContent = moreLabel(items.length); // provisional, realistic width
    moreItem.hidden = false;

    const checkedIndex = items.findIndex((item) => item.querySelector('input:checked'));
    let cutoff = this.#geometricCutoff(items, maxRows);

    for (let guard = items.length; guard > 0; guard--) {
      this.#applyCutoff(items, cutoff, checkedIndex);
      const visible = items.filter((item) => !item.classList.contains('jgd-option-hidden'));
      if (this.#rowCount([...visible, moreItem]) <= maxRows || cutoff <= 1) break;
      cutoff--;
    }

    const hiddenCount = items.filter((item) => item.classList.contains('jgd-option-hidden')).length;
    if (!staticLabel) moreButton.textContent = moreLabel(hiddenCount);
    moreItem.hidden = hiddenCount === 0;

    this.#measuring = false;
  }

  // --- row mode (color tiles: one line, scroll baseline) ---------------------

  #measureRow() {
    if (this.#measuring) return;
    if (this.hasAttribute('expanded')) return;
    // Not laid out yet (e.g. inside a still-closed quick-add dialog); the
    // ResizeObserver re-runs this once the element gets real dimensions.
    if (this.clientWidth === 0) return;

    const list = this.querySelector('.jgd-option-list__list');
    const items = [...this.querySelectorAll('li.jgd-variant-option__item')];
    const moreControl = this.querySelector('.jgd-option-list__more');
    const moreButton = moreControl?.querySelector('button');
    if (!list || !items.length || !moreControl || !moreButton) return;

    this.#measuring = true;

    const staticLabel = this.hasAttribute('static-more-label');
    const labelBase = this.getAttribute('more-label');
    /** @param {number} count */
    const moreLabel = (count) => (labelBase ? `${labelBase} (+${count})` : `+${count}`);
    // The tile-sized in-row "+N" takes exactly one tile slot; the text-label
    // control sits below the row and doesn't compete for row space.
    const inRow = moreControl.closest('.jgd-option-list__list') !== null;

    // Reset to the baseline: every tile visible in one scrollable line.
    for (const item of items) item.classList.remove('jgd-option-hidden');
    moreControl.hidden = true;
    list.scrollLeft = 0;

    // How many tiles fit the line completely?
    const listLeft = list.getBoundingClientRect().left;
    let fit = 0;
    for (const item of items) {
      if (item.getBoundingClientRect().right - listLeft <= list.clientWidth + 1) fit++;
      else break;
    }

    if (fit < items.length) {
      const slots = Math.max(1, inRow ? fit - 1 : fit);
      const checkedIndex = items.findIndex((item) => item.querySelector('input:checked'));
      this.#applyCutoff(items, slots, checkedIndex);

      const hiddenCount = items.filter((item) => item.classList.contains('jgd-option-hidden')).length;
      if (!staticLabel) moreButton.textContent = moreLabel(hiddenCount);
      moreControl.hidden = false;
    }

    // Switches the baseline's overflow-x from auto to hidden (CSS).
    this.setAttribute('measured', '');
    this.#measuring = false;
  }

  /**
   * Number of flex rows the given elements occupy (grouped by offsetTop).
   * @param {HTMLElement[]} elements
   */
  #rowCount(elements) {
    /** @type {number[]} */
    const tops = [];
    for (const element of elements) {
      if (element.hidden) continue;
      const top = element.offsetTop;
      if (!tops.some((t) => Math.abs(t - top) < 2)) tops.push(top);
    }
    return tops.length;
  }

  /**
   * Index of the first item that starts beyond the allowed rows
   * (all items and the "+N" button are visible when this runs).
   * @param {HTMLElement[]} items
   * @param {number} maxRows
   */
  #geometricCutoff(items, maxRows) {
    /** @type {number[]} */
    const tops = [];
    for (const [index, item] of items.entries()) {
      const top = item.offsetTop;
      if (!tops.some((t) => Math.abs(t - top) < 2)) tops.push(top);
      if (tops.length > maxRows) return index;
    }
    return items.length;
  }

  /**
   * Hide items from `cutoff` on; a checked item beyond the cutoff takes the
   * last visible slot instead.
   * @param {HTMLElement[]} items
   * @param {number} cutoff
   * @param {number} checkedIndex
   */
  #applyCutoff(items, cutoff, checkedIndex) {
    const reserveForChecked = checkedIndex >= cutoff;
    items.forEach((item, index) => {
      let hidden = index >= cutoff;
      if (reserveForChecked) {
        if (index === checkedIndex) hidden = false;
        else if (index === cutoff - 1) hidden = true;
      }
      item.classList.toggle('jgd-option-hidden', hidden);
    });
  }
}

// After a variant change the section is re-rendered (morphed): server HTML
// carries no expanded state and, in rows mode, no hidden classes — re-sync.
document.addEventListener('variant:update', () => {
  requestAnimationFrame(() => {
    for (const list of document.querySelectorAll('jgd-option-list')) {
      if (list instanceof JgdOptionList) list.sync();
    }
  });
});

if (!customElements.get('jgd-option-list')) {
  customElements.define('jgd-option-list', JgdOptionList);
}
